/**
 * ADR rules (Technical Specifications, section 9.4; FR-PAR-17, FR-PAR-18, FR-FIA-02, BR-11).
 * Pure functions with table-driven tests; rule sets are selected by ADR edition.
 */
/** Entries whose document gives the class number "9" instead of their labels (5.4.1.1.1 c). */
const CLASS_9_BATTERIES = ['3090', '3091', '3480', '3481', '3551', '3552', '3556', '3557', '3558'];
/** Entries that never carry the "DANGEREUX POUR L'ENVIRONNEMENT" statement (5.4.1.1.18). */
const ENVIRONMENT_ENTRIES = ['3077', '3082'];
/**
 * The hazard of an entry on the transport document (ADR 5.4.1.1.1 c): for class 1 the
 * classification code, followed in brackets by the labels other than 1, 1.4, 1.5 and 1.6; for
 * class 7 and the batteries of UN 3090 and the like, the class number; otherwise the label
 * numbers, those after the first in brackets, or the class when the entry has no label.
 */
export function hazardDescription(e) {
    if (e.classCode === '1') {
        const others = e.labels.filter((l) => !['1', '1.4', '1.5', '1.6'].includes(l));
        const code = e.classificationCode ?? e.classCode;
        return others.length ? `${code} (${others.join(', ')})` : code;
    }
    if (e.classCode === '7')
        return '7';
    if (CLASS_9_BATTERIES.includes(e.unNumber))
        return '9';
    const [first, ...rest] = e.labels;
    if (!first)
        return e.classCode;
    return rest.length ? `${first} (${rest.join(', ')})` : first;
}
export const EMPTY_PACKAGING_KINDS = ['EMBALLAGE', 'RECIPIENT', 'GRV', 'GRAND_EMBALLAGE'];
/** The words of ADR 5.4.1.1.6.2.1 for each kind of empty means of containment. */
export const EMPTY_PACKAGING_WORDS = {
    EMBALLAGE: 'EMBALLAGE VIDE',
    RECIPIENT: 'RÉCIPIENT VIDE',
    GRV: 'GRV VIDE',
    GRAND_EMBALLAGE: 'GRAND EMBALLAGE VIDE',
};
/** Classes whose empty uncleaned packagings ADR 1.1.3.5 can exempt. */
export const EXEMPT_1_1_3_5_CLASSES = ['2', '3', '4.1', '5.1', '6.1', '8', '9'];
export const EXEMPT_1_1_3_5_STATEMENT = "NON SOUMIS À L'ADR (1.1.3.5)";
/** Whether ADR 1.1.3.5 can exempt empty uncleaned packagings that held this entry. */
export function exemption1135Applies(e) {
    return EXEMPT_1_1_3_5_CLASSES.includes(e.classCode);
}
/**
 * The transport category for the 1.1.3.6 regime: an empty uncleaned packaging takes category 0
 * when its last contents were of category 0, and category 4 otherwise (ADR 1.1.3.6.1).
 */
export function effectiveTransportCategory(adr, category) {
    if (!adr.emptyPackaging || category === null)
        return category;
    return category === 0 ? 0 : 4;
}
export const ENVIRONMENTALLY_HAZARDOUS_STATEMENT = "DANGEREUX POUR L'ENVIRONNEMENT";
export const WASTE_2_1_3_5_5_STATEMENT = 'DÉCHETS CONFORMES AU 2.1.3.5.5';
export const ESTIMATED_QUANTITY_STATEMENT = 'QUANTITÉ ESTIMÉE CONFORMÉMENT AU 5.4.1.1.3.2';
/**
 * Builds the transport document mention of a waste (ADR 5.4.1.1.1 and 5.4.1.1.3):
 * UN number, proper shipping name (with technical name), class, packing group, tunnel code,
 * then the statements that apply. Returns null for a waste not identified in the ADR.
 */
export function buildAdrMention(adr, options = {}) {
    if (!adr.identified)
        return null;
    if (adr.mentionOverride?.trim()) {
        return withEstimated(adr.mentionOverride.trim(), options);
    }
    const e = adr.entry;
    if (!e)
        throw new Error('An ADR-identified waste needs an ADR entry');
    if (e.carriage === 'PROHIBITED')
        throw new Error(`ADR prohibits the carriage of UN ${e.unNumber}`);
    if (e.carriage === 'NOT_SUBJECT')
        throw new Error(`UN ${e.unNumber} is not subject to ADR`);
    // ADR 2.1.3.5.5: a waste classified through it carries the statement instead of the technical name
    if (e.requiresTechnicalName && !adr.conformsTo2_1_3_5_5 && !adr.technicalName?.trim()) {
        throw new Error(`UN ${e.unNumber} requires a technical name`);
    }
    // ADR 3.1.2: of alternative names joined by a lowercase "ou" in table A, only one is used
    const alternatives = shippingNameAlternatives(e.properShippingName);
    const choice = adr.shippingNameChoice?.trim();
    if (choice && !alternatives.some((a) => a.toUpperCase() === choice.toUpperCase())) {
        throw new Error(`"${choice}" is not a proper shipping name of UN ${e.unNumber}`);
    }
    let name = (choice ?? alternatives[0]).toUpperCase();
    if (adr.emptyPackaging) {
        const words = EMPTY_PACKAGING_WORDS[adr.emptyPackagingKind ?? 'EMBALLAGE'];
        if (adr.exempt1135) {
            if (!exemption1135Applies(e)) {
                throw new Error(`ADR 1.1.3.5 does not exempt packagings that held class ${e.classCode}`);
            }
            return withEstimated(`${words}, NON NETTOYÉ, ${EXEMPT_1_1_3_5_STATEMENT}`, options);
        }
        if ((adr.emptyPackagingDescription ?? 'SHORT') === 'SHORT') {
            // 5.4.1.1.6.2.1: the words, then the hazard of the last contents (the class alone for class 2)
            const hazard = e.classCode === '2' ? '2' : hazardDescription(e);
            const short = [`${words}, ${hazard}`, e.tunnelCode ? `(${e.tunnelCode})` : null]
                .filter(Boolean)
                .join(', ');
            return withEstimated(short, options);
        }
    }
    else if (!adr.conformsTo2_1_3_5_5 && !name.startsWith('DÉCHET')) {
        name = `DÉCHET ${name}`;
    }
    if (adr.technicalName?.trim()) {
        name = `${name} (${adr.technicalName.trim().toUpperCase()})`;
    }
    const parts = [`UN ${e.unNumber} ${name}`, hazardDescription(e)];
    if (e.packingGroup)
        parts.push(e.packingGroup);
    if (e.tunnelCode)
        parts.push(`(${e.tunnelCode})`);
    // 5.4.1.1.6.1: the full description of the last contents, then the empty uncleaned words
    if (adr.emptyPackaging)
        parts.push('VIDE, NON NETTOYÉ');
    let mention = parts.join(', ');
    if (adr.environmentallyHazardous && !ENVIRONMENT_ENTRIES.includes(e.unNumber)) {
        mention += `, ${ENVIRONMENTALLY_HAZARDOUS_STATEMENT}`;
    }
    if (adr.conformsTo2_1_3_5_5)
        mention += `, ${WASTE_2_1_3_5_5_STATEMENT}`;
    return withEstimated(mention, options);
}
/** The alternatives of a proper shipping name, joined by a lowercase "ou" in table A (ADR 3.1.2.1). */
export function shippingNameAlternatives(properShippingName) {
    return properShippingName
        .split(/\s+ou\s+/)
        .map((a) => a.trim())
        .filter(Boolean);
}
function withEstimated(mention, options) {
    return options.estimatedQuantity ? `${mention}\n${ESTIMATED_QUANTITY_STATEMENT}` : mention;
}
/** What sets apart the lines of one UN number and packing group for a waste. */
const variantKey = (e) => [
    e.properShippingName,
    hazardDescription(e),
    e.tunnelCode ?? '',
    e.transportCategory ?? '',
    e.requiresTechnicalName ? 'T' : '',
    e.carriage ?? 'ALLOWED',
].join('|');
/**
 * The lines of table A a waste must choose between: those of its packing group when they differ
 * in what the document states or in the transport category (UN 1950 flammable or not, UN 1263
 * viscous or not). Empty when one line fits.
 */
export function adrVariants(entries, packingGroup) {
    const lines = entries.filter((e) => (e.packingGroup ?? undefined) === packingGroup);
    return new Set(lines.map(variantKey)).size > 1 ? lines : [];
}
/**
 * The line of table A of a waste: the variant it names when there is one (by its description),
 * otherwise the first line of its packing group, or of the UN number.
 */
export function pickAdrEntry(entries, block) {
    const pg = block.packingGroup ?? undefined;
    const lines = entries.filter((e) => !pg || e.packingGroup === pg);
    const named = block.variant ? lines.find((e) => e.description === block.variant) : undefined;
    return named ?? lines[0] ?? entries[0];
}
/** Pictograms to print on the label of a waste (FR-FIA-07). */
export function labelPictograms(adr) {
    if (!adr.identified || !adr.entry)
        return [];
    const set = new Set(adr.entry.labels);
    if (adr.environmentallyHazardous)
        set.add('ENV');
    return [...set];
}
/** Factors of ADR 1.1.3.6.3 per transport category. Category 4 is unlimited, category 0 forbids the exemption. */
export const TRANSPORT_CATEGORY_FACTORS = {
    0: Number.POSITIVE_INFINITY,
    1: 50,
    2: 3,
    3: 1,
    4: 0,
};
export const REGIME_1_1_3_6_THRESHOLD = 1000;
/** Computes the 1.1.3.6 exemption of a chargement from its ADR lines (FR-FIA-02). */
export function computeRegime1136(lines) {
    let points = 0;
    const blockingLines = [];
    lines.forEach((line, index) => {
        if (line.quantity < 0)
            throw new Error(`Negative quantity on line ${index}`);
        if (line.quantity === 0)
            return;
        if (line.transportCategory === 0) {
            blockingLines.push(index);
            return;
        }
        points += line.quantity * TRANSPORT_CATEGORY_FACTORS[line.transportCategory];
    });
    return {
        points,
        threshold: REGIME_1_1_3_6_THRESHOLD,
        exemptionPossible: blockingLines.length === 0 && points <= REGIME_1_1_3_6_THRESHOLD,
        blockingLines,
    };
}
//# sourceMappingURL=adr.js.map